<template>
  <div class="bg-white rounded-lg shadow-sm border border-gray-200">
    <div class="p-4 border-b border-gray-200 flex items-center space-x-3">
      <span class="material-symbols-outlined text-xl text-primary">settings</span>
      <h2 class="text-lg font-semibold text-gray-800">Subscription Actions</h2>
    </div>
    
    <div class="p-4 sm:p-6 space-y-4">
      <!-- Upgrade Plan -->
      <div
        v-if="profile?.plan?.name !== 'TrendzTube Pro'"
        class="group flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-gradient-to-r from-primary/5 to-primary/10 rounded-lg transition-all duration-200 hover:shadow-md border border-primary/20"
      >
        <div class="flex items-start sm:items-center space-x-3 mb-4 sm:mb-0">
          <div class="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm transition-all group-hover:shadow-md group-hover:scale-105">
            <span class="material-symbols-outlined text-primary text-xl">upgrade</span>
          </div>
          <div class="flex-1">
            <h4 class="font-semibold text-gray-900 text-left mb-1">
              Upgrade Your Plan
            </h4>
            <p class="text-sm text-gray-600 text-left leading-relaxed">
              Get more features and higher limits with our premium plans
            </p>
          </div>
        </div>
        <router-link
          to="/upgrade"
          class="w-full sm:w-auto sm:flex-shrink-0 inline-flex items-center justify-center px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-all duration-200 font-semibold shadow-sm hover:shadow-md transform hover:scale-105 text-center"
        >
          <span class="material-symbols-outlined text-sm mr-2">rocket_launch</span>
          Upgrade Now
        </router-link>
      </div>

      <!-- Cancel Subscription -->
      <div v-if="profile?.plan?.active && profile?.plan?.name !== 'free'" 
           class="group flex flex-col sm:flex-row sm:items-start justify-between p-4 bg-gradient-to-r from-red-50 to-red-100/50 rounded-lg border border-red-200 hover:shadow-md transition-all duration-200">
        <div class="flex items-start space-x-3 mb-4 sm:mb-0 flex-1">
          <div class="w-12 h-12 bg-white rounded-xl flex items-center justify-center group-hover:bg-red-50 transition-all shadow-sm">
            <span class="material-symbols-outlined text-red-600 text-xl">cancel</span>
          </div>
          <div class="flex-1">
            <h4 class="font-semibold text-red-900 text-left mb-2">Cancel Subscription</h4>
            <p class="text-sm text-red-700 text-left leading-relaxed mb-3">
              Cancelling will immediately downgrade you to the Free plan and you will lose access to premium features right away.
            </p>
            <div class="bg-white/70 rounded-lg p-3 border border-red-200/50">
              <p class="text-xs text-gray-600 text-left leading-relaxed">
                <span class="material-symbols-outlined text-xs text-amber-600 align-middle mr-1">lightbulb</span>
                <strong>Tip:</strong> Your plan renews on {{ formatDate(profile.plan.renewsAt) }}. For best value, consider cancelling a day before renewal (e.g., on {{ formatDate(getRecommendedCancelDate(profile.plan.renewsAt)) }}).
              </p>
            </div>
          </div>
        </div>
        <button 
          @click="handleCancelClick"
          :disabled="loading"
          class="w-full sm:w-auto sm:flex-shrink-0 inline-flex items-center justify-center px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all duration-200 disabled:opacity-50 font-semibold shadow-sm hover:shadow-md transform hover:scale-105 text-center"
        >
          <span class="material-symbols-outlined text-sm mr-2">warning</span>
          Cancel Plan
        </button>
      </div>

      <!-- Contact Support Button -->
      <div class="relative">
        <button
          @click="$emit('contact-support')"
          type="button"
          class="w-full flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-primary to-primary-dark text-white rounded-lg hover:from-primary-dark hover:to-primary transition-all duration-200 font-semibold shadow-sm hover:shadow-md transform hover:scale-[1.02] border border-primary/20"
        >
          <span class="material-symbols-outlined text-xl">support_agent</span>
          <span>Contact Support</span>
          <span class="material-symbols-outlined text-sm opacity-70">arrow_outward</span>
        </button>
        <div class="absolute inset-0 bg-gradient-to-r from-primary/20 to-primary-dark/20 rounded-lg blur-xl -z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SubscriptionActions',
  props: {
    profile: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  emits: ['cancel-subscription', 'contact-support'],
  methods: {
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },

    getRecommendedCancelDate(renewsAt) {
      if (!renewsAt) return '';
      const date = new Date(renewsAt);
      date.setDate(date.getDate() - 1);
      return date;
    },

    handleCancelClick() {
      this.$emit('cancel-subscription');
    }
  }
};
</script>

<style scoped>
/* Enhanced visual effects */
@media (hover: hover) {
  .group:hover .transform {
    transform: scale(1.02);
  }
}

/* Responsive spacing adjustments */
@media (max-width: 640px) {
  .space-y-4 > * + * {
    margin-top: 1rem;
  }
  
  .p-4 {
    padding: 1rem;
  }
  
  .sm\:p-6 {
    padding: 1rem;
  }
}

/* Smooth gradients */
.bg-gradient-to-r {
  background-image: linear-gradient(to right, var(--tw-gradient-stops));
}

/* Enhanced shadows */
.shadow-sm {
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}

.hover\:shadow-md:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

/* Improved focus states */
button:focus {
  outline: 2px solid rgba(66, 153, 225, 0.5);
  outline-offset: 2px;
}
</style>
